// Shared layout for the three segment landing pages
// (/pour/editeurs-solo, /pour/agences-medias, /pour/groupes-presse).
//
// Each segment page passes its hero copy, three pain points, three
// solutions, optional extra sections (rendered via `children`) and a
// final CTA target. The layout takes care of SEO head, breadcrumbs,
// section rhythm and JSON-LD.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { CTABanner } from "@/components/marketing/CTABanner";
import { Rule } from "@/components/marketing/Rule";
import { buildBreadcrumbJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import { withUtm } from "@/lib/marketing-utm";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

export interface PainPoint {
  title: string;
  body: string;
}

export interface SolutionPoint {
  title: string;
  body: string;
}

export interface MarketingSegmentLayoutProps {
  /** SEO title + breadcrumb leaf. */
  seoTitle: string;
  seoDescription: string;
  /** Path under which this page is served, used for canonical + breadcrumb. */
  path: string;
  breadcrumbName: string;

  /** Hero block. */
  eyebrow: string;
  heroTitle: string;
  heroLead: string;

  /** Three pain points (rendered as two-up grid on md, three-up on lg). */
  pains: PainPoint[];
  painsHeading: string;

  /** Three solution points mirroring the pains. */
  solutions: SolutionPoint[];
  solutionsHeading: string;

  /** UTM campaign used for outbound /tarifs CTAs from this page. */
  utmCampaign: string;

  /** Final CTA banner. */
  ctaHeadline: string;
  ctaSubhead: string;
  primaryCtaLabel: string;
  primaryCtaTo: string;
  secondaryCtaLabel: string;
  secondaryCtaTo: string;

  /** Optional sections rendered between solutions and the CTA banner. */
  children?: ReactNode;
}

export function MarketingSegmentLayout({
  seoTitle,
  seoDescription,
  path,
  breadcrumbName,
  eyebrow,
  heroTitle,
  heroLead,
  pains,
  painsHeading,
  solutions,
  solutionsHeading,
  utmCampaign,
  ctaHeadline,
  ctaSubhead,
  primaryCtaLabel,
  primaryCtaTo,
  secondaryCtaLabel,
  secondaryCtaTo,
  children,
}: MarketingSegmentLayoutProps) {
  const { t, i18n } = useTranslation();
  const locale = useLocale();

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: breadcrumbName, path },
    ]),
  ];

  return (
    <>
      <SeoHead title={seoTitle} description={seoDescription} path={path} locale={locale} jsonLd={jsonLd} />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">
            {heroTitle}
          </DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{heroLead}</p>
        </div>
      </Section>

      <Rule />

      <Section padding="md">
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {painsHeading}
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {pains.map((p, i) => (
            <article key={i} className="border border-rule bg-background p-6">
              <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden />
              <h3 className="mt-4 text-base font-medium tracking-tight">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section padding="md">
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {solutionsHeading}
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {solutions.map((s, i) => (
            <article key={i} className="border border-rule bg-paper p-6 text-ink">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
              <h3 className="mt-4 text-base font-medium tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/70">{s.body}</p>
            </article>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to={withUtm("/tarifs", { source: "segment", campaign: utmCampaign })}
            className="inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline"
          >
            {t("marketing.cta.viewPricing")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </Section>

      {children}

      <CTABanner
        headline={ctaHeadline}
        subhead={ctaSubhead}
        primary={{ label: primaryCtaLabel, to: withUtm(primaryCtaTo, { source: "segment", campaign: utmCampaign }) }}
        secondary={{ label: secondaryCtaLabel, to: secondaryCtaTo }}
        tone="ink"
      />
    </>
  );
}
